/**
 * AI Service - Real backend calls backed by AWS Bedrock (Claude Haiku 4.5).
 *
 * All public functions hit the `/ai/*` endpoints on the backend Lambda. If
 * `REACT_APP_USE_MOCK_AI=true`, we fall back to deterministic heuristics so the
 * product can be demoed offline. Interfaces are stable; consumers (AIAssistant
 * UI kit, AssessmentForm) don't need to change when we flip the flag.
 */

import axios, { AxiosInstance } from 'axios';

// ── Public types ─────────────────────────────────────────────────────────────

export interface AI5WHAnalysis {
  who: { present: boolean; value: string };
  what: { present: boolean; value: string };
  where: { present: boolean; value: string };
  when: { present: boolean; value: string };
  why: { present: boolean; value: string };
  how: { present: boolean; value: string };
  score: number; // 0-6
  confidence: number; // 0-100
  summary: string;
}

export interface AIObligationMapping {
  confidence: number;
  items: Array<{
    id: string;
    name: string;
    recommendation: 'Yes' | 'No' | 'Review';
    rationale: string;
  }>;
  note: string;
}

export interface AIPrefillResponse {
  questionId: string;
  response: 'Y' | 'N' | 'NA';
  confidence: number; // 0-100
  rationale: string;
}

export interface AICMAQuestionPrefill {
  questionId: string;
  assessment: string;
  confidence: number;
  sufficient: boolean;
  proposedChange?: string;
}

export interface AIRecommendation {
  dimension: 'WHO' | 'WHEN' | 'HOW' | 'WHERE' | 'WHAT';
  suggestion: string;
  rationale: string;
  confidence: number;
}

export interface AIRationale {
  rationale: string;
  confidence: number;
}

// ── CMA fallback helper ───────────────────────────────────────────────────────

function cmaFallbackForQuestion(id: string): { assessment: string; proposedChange: string } {
  const isMonitoring = id.includes('_M_');
  const subject = isMonitoring ? 'monitoring' : 'control';
  const dim = id.split('_').pop()?.toUpperCase() || '';
  const map: Record<string, { assessment: string; proposedChange: string }> = {
    WHO: {
      assessment: `WHO is not specified — the description does not identify who performs this ${subject} or their authority level.`,
      proposedChange: `Specify the role, team, or individual responsible for performing this ${subject}, their authority level, required competencies, and independence from transaction processing.`,
    },
    WHEN: {
      assessment: `WHEN is not specified — the description does not state the frequency or process stage at which this ${subject} is performed.`,
      proposedChange: `State the frequency (e.g. daily, weekly) and the process stage at which this ${subject} is performed, and clarify whether it is preventative or detective.`,
    },
    HOW: {
      assessment: `HOW is not specified — the description does not describe procedures, escalation path, or execution mechanism for this ${subject}.`,
      proposedChange: `Describe the procedures governing this ${subject}, the escalation path for exceptions or failures, and how adequate coverage is maintained as transaction volumes grow.`,
    },
    WHERE: {
      assessment: `WHERE is not specified — the description does not state where evidence of this ${subject} is documented or stored.`,
      proposedChange: `Specify the system or location where ${subject} execution is evidenced, how maker and checker document their actions, and the applicable record retention policy.`,
    },
    WHAT: {
      assessment: `WHAT is not specified — the description does not confirm what risk this ${subject} mitigates or whether it meets its ${subject} objective.`,
      proposedChange: `Clarify what specific risk this ${subject} mitigates, whether it is preventative or detective in nature, and confirm it meets the stated ${subject} objective.`,
    },
  };
  return map[dim] || {
    assessment: 'Insufficient information in the description to assess this question.',
    proposedChange: 'Review the description and provide details that directly address this question.',
  };
}

// ── Transport ────────────────────────────────────────────────────────────────

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const USE_MOCK = (process.env.REACT_APP_USE_MOCK_AI || '').toLowerCase() === 'true';

let client: AxiosInstance | null = null;
function getClient(): AxiosInstance {
  if (client) return client;
  client = axios.create({
    baseURL: API_BASE_URL,
    headers: { 'Content-Type': 'application/json' },
    timeout: 30000,
  });
  client.interceptors.request.use(async config => {
    try {
      const { Auth } = await import('aws-amplify');
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      config.headers.Authorization = `Bearer ${token}`;
    } catch {
      /* unauthenticated dev mode */
    }
    return config;
  });
  return client;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await getClient().post<T>(path, body);
  return res.data;
}

// ── 1. 5W+H analysis ─────────────────────────────────────────────────────────

export async function analyzeControlDescription(description: string): Promise<AI5WHAnalysis> {
  if (USE_MOCK) return mockAnalyze(description);
  try {
    return await post<AI5WHAnalysis>('/ai/analyze-control', { description });
  } catch (err) {
    console.error('analyzeControlDescription failed', err);
    return mockAnalyze(description);
  }
}

// ── 2. Obligation mapping ────────────────────────────────────────────────────

export async function mapObligations(description: string): Promise<AIObligationMapping> {
  if (USE_MOCK) return mockObligations(description);
  try {
    return await post<AIObligationMapping>('/ai/map-obligations', { description });
  } catch (err) {
    console.error('mapObligations failed', err);
    return mockObligations(description);
  }
}

// ── 3. CMDA pre-fill ─────────────────────────────────────────────────────────

export async function prefillCMDAAnswers(
  questions: Array<{ id: string; question: string; guidance?: string }> | string[],
  description: string
): Promise<AIPrefillResponse[]> {
  // Normalise: accept either full question objects or bare ID strings
  const questionObjects = (questions as any[]).map((q: any) =>
    typeof q === 'string' ? { id: q, question: q } : q
  );
  const questionIds = questionObjects.map((q: any) => q.id);
  if (USE_MOCK) return mockPrefill(questionIds, description);
  try {
    const res = await post<{ items: AIPrefillResponse[] }>('/ai/prefill-cmda', {
      questions: questionObjects,
      questionIds,
      description,
    });
    return res.items || [];
  } catch (err) {
    console.error('prefillCMDAAnswers failed', err);
    return mockPrefill(questionIds, description);
  }
}

// ── 3b. CMA per-question pre-fill from description ───────────────────────────

export async function prefillCMAQuestions(
  questions: Array<{ id: string; question: string; guidance?: string }>,
  description: string
): Promise<AICMAQuestionPrefill[]> {
  const fallback = (): AICMAQuestionPrefill[] =>
    questions.map(q => ({ questionId: q.id, ...cmaFallbackForQuestion(q.id), confidence: 0, sufficient: false }));
  if (USE_MOCK) return fallback();
  if (!description || !description.trim()) return fallback();
  try {
    const res = await post<{ items: AICMAQuestionPrefill[] }>('/ai/prefill-cma-questions', {
      description,
      questions,
    });
    return res.items || [];
  } catch (err) {
    console.error('prefillCMAQuestions failed', err);
    return fallback();
  }
}

// ── 4. CMA suggestions ───────────────────────────────────────────────────────

export async function suggestCMARecommendations(context: string): Promise<AIRecommendation[]> {
  if (USE_MOCK) return mockCmaSuggestions();
  try {
    const res = await post<{ items: AIRecommendation[] }>('/ai/suggest-cma', { context });
    return res.items || [];
  } catch (err) {
    console.error('suggestCMARecommendations failed', err);
    return mockCmaSuggestions();
  }
}

// ── 5. Final rationale ───────────────────────────────────────────────────────

export async function generateRationale(params: {
  type: 'CMDA' | 'CMA';
  controlAdequate: boolean;
  monitoringAdequate: boolean;
  score?: { control: number; monitoring: number };
}): Promise<AIRationale> {
  if (USE_MOCK) return mockRationale(params);
  try {
    return await post<AIRationale>('/ai/generate-rationale', params);
  } catch (err) {
    console.error('generateRationale failed', err);
    return mockRationale(params);
  }
}

// ── Deterministic mocks (fallback + REACT_APP_USE_MOCK_AI=true) ─────────────

const hasAny = (text: string, kw: string[]) =>
  kw.some(k => text.toLowerCase().includes(k.toLowerCase()));

function mockAnalyze(description: string): AI5WHAnalysis {
  const text = description || '';
  const who = hasAny(text, ['agent', 'team', 'manager', 'operator', 'unit', 'analyst', 'officer', 'performer']);
  const what = hasAny(text, ['review', 'approve', 'reconcile', 'verify', 'check', 'monitor', 'remediation', 'validate']);
  const where = hasAny(text, ['salesforce', 'oracle', 'sharepoint', 'pega', 'zendesk', 'system', 'mis', 'cisco', 'database']);
  const when = hasAny(text, ['daily', 'weekly', 'monthly', 'quarterly', 'annually', 'after', 'upon', 'every']);
  const why = hasAny(text, ['compliance', 'regulation', 'risk', 'regulatory', 'obligation', 'ensure', 'mitigate']);
  const how = hasAny(text, ['submit', 'escalate', 'log', 'update', 'locate', 'process', 'execute', 'perform', 'document']);
  const dims = [who, what, where, when, why, how];
  const score = dims.filter(Boolean).length;
  const confidence = Math.min(95, 60 + score * 6);
  const present = (v: boolean, label: string) => ({
    present: v,
    value: v ? `${label} identified` : `${label} missing`,
  });
  return {
    who: present(who, 'Control performer'),
    what: present(what, 'Control activity'),
    where: present(where, 'Evidence location'),
    when: present(when, 'Frequency'),
    why: present(why, 'Regulatory driver'),
    how: present(how, 'Execution path'),
    score,
    confidence,
    summary:
      score === 6
        ? 'Description meets standard. All 6 dimensions of the 5W+H framework are present.'
        : `${score}/6 dimensions present. Consider adding detail for: ${[
            !who && 'WHO',
            !what && 'WHAT',
            !where && 'WHERE',
            !when && 'WHEN',
            !why && 'WHY',
            !how && 'HOW',
          ]
            .filter(Boolean)
            .join(', ')}.`,
  };
}

function mockObligations(description: string): AIObligationMapping {
  const text = description || '';
  const items: AIObligationMapping['items'] = [];
  if (hasAny(text, ['regulation e', '12 cfr', 'electronic funds', 'remittance'])) {
    items.push({
      id: '12 CFR 1005.34',
      name: 'Regulation E (12 CFR Part 1005)',
      recommendation: 'Yes',
      rationale: 'Control directly addresses §1005.34 cancellation procedures.',
    });
  }
  if (hasAny(text, ['disclosure', 'accuracy', 'remittance'])) {
    items.push({
      id: '12 CFR 1005.31',
      name: 'Remittance Transfers: Disclosure Requirements',
      recommendation: 'Yes',
      rationale: 'Disclosure and accuracy requirements are addressed by this control.',
    });
  }
  if (items.length === 0) {
    items.push({
      id: 'N/A',
      name: 'No direct obligation match',
      recommendation: 'Review',
      rationale: 'No keyword match found. Manually confirm regulatory mapping.',
    });
  }
  return {
    confidence: items[0].recommendation === 'Yes' ? 94 : 72,
    items,
    note: 'CDA questions have been pre-filled based on this analysis.',
  };
}

function mockPrefill(questionIds: string[], description: string): AIPrefillResponse[] {
  const rich = (description || '').trim().length > 200;
  return questionIds.map((qid, i) => {
    if (!rich) {
      return {
        questionId: qid,
        response: 'NA' as 'Y' | 'N' | 'NA',
        confidence: 0,
        rationale: 'Unable to determine from the provided description — please review manually.',
      };
    }
    const mustBeNo = qid === 'C6' || qid === 'M7' || qid === 'M8';
    const base = mustBeNo ? 'N' : 'Y';
    const confidence = Math.min(96, 80 + ((i * 3) % 10));
    return {
      questionId: qid,
      response: base as 'Y' | 'N' | 'NA',
      confidence,
      rationale: mustBeNo
        ? 'Description does not flag a weakness for this exception question; answered "No".'
        : 'Description provides concrete evidence (performer, system, frequency) supporting "Yes".',
    };
  });
}

function mockCmaSuggestions(): AIRecommendation[] {
  const dims: AIRecommendation['dimension'][] = ['WHO', 'WHEN', 'HOW', 'WHERE', 'WHAT'];
  const blurbs: Record<AIRecommendation['dimension'], string> = {
    WHO: 'Current performer is appropriate. Consider adding a second reviewer for segregation of duties on high-risk cases.',
    WHEN: 'Frequency is acceptable. Recommend real-time or near-real-time triggers for critical events to reduce detection lag.',
    HOW: 'Procedures are documented. Recommend converting detective steps into preventative system-enforced controls where possible.',
    WHERE: 'Evidence is captured across multiple systems. Consider consolidating to a single source of truth for audit efficiency.',
    WHAT: 'Control is currently detective. Add preventative control points upstream to reduce risk exposure and avoid remediation.',
  };
  return dims.map((d, i) => ({
    dimension: d,
    suggestion: blurbs[d],
    rationale: 'Based on control description, regulatory context, and industry best practice.',
    confidence: 85 + ((i * 2) % 10),
  }));
}

function mockRationale(params: {
  type: 'CMDA' | 'CMA';
  controlAdequate: boolean;
  monitoringAdequate: boolean;
}): AIRationale {
  const status = params.controlAdequate && params.monitoringAdequate ? 'Adequate' : 'Inadequate';
  const rationale =
    params.type === 'CMDA'
      ? `This control was assessed as ${status}. The control description has ${
          params.controlAdequate ? 'sufficient' : 'gaps in'
        } coverage across the 5W+H dimensions, and monitoring is ${
          params.monitoringAdequate ? 'adequate' : 'insufficient'
        } to detect exceptions in a timely manner. ${
          status === 'Inadequate'
            ? 'Recommend strengthening preventative controls and reducing detection lag.'
            : 'Existing design meets regulatory expectations.'
        }`
      : `Assessment complete. ${
          params.controlAdequate ? 'Control design is sound.' : 'Control design requires refinement.'
        } ${
          params.monitoringAdequate
            ? 'Monitoring approach is effective.'
            : 'Monitoring approach requires additional coverage.'
        }`;
  return { rationale, confidence: 91 };
}
